import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, MenuItem,
  Select, FormControl, InputLabel, Card, CardContent,
  Slider, Divider, Tooltip, InputAdornment, Snackbar,
} from '@mui/material';
import { FileDownload, FilterAlt, AttachMoney, TrendingUp, Warning, CheckCircle, Schedule } from '@mui/icons-material';
import { apiFetch } from '../../services/apiFetch';

interface ReviewRow {
  employeeId: string;
  firstName: string;
  lastName: string;
  employeeNumber?: string;
  department?: string;
  departmentId?: string;
  position?: string;
  startDate?: string;
  currentSalary?: number;
  currency?: string;
  frequency?: string;
  previousSalary?: number;
  lastChangeType?: string;
  lastChangeDate?: string;
  lastChangePercent?: number;
  daysSinceChange?: number;
  yearsAtCompany?: number;
  reviewStatus: 'overdue' | 'due_soon' | 'recent' | 'no_data';
  /** proposed increase % — local state only */
  proposedPct?: number;
}

interface Department { id: string; name: string; }

const STATUS_CONFIG = {
  overdue:   { label: 'Overdue',   color: 'error'   as const, icon: <Warning fontSize="small" /> },
  due_soon:  { label: 'Due Soon',  color: 'warning' as const, icon: <Schedule fontSize="small" /> },
  recent:    { label: 'Recent',    color: 'success' as const, icon: <CheckCircle fontSize="small" /> },
  no_data:   { label: 'No data',   color: 'default' as const, icon: <AttachMoney fontSize="small" /> },
};

function annualSalary(row: ReviewRow): number {
  if (!row.currentSalary) return 0;
  if (row.frequency === 'annual') return Number(row.currentSalary);
  if (row.frequency === 'hourly') return Number(row.currentSalary) * 2080;
  return Number(row.currentSalary) * 12; // monthly default
}

function fmt(n: number, currency = 'HKD') {
  return new Intl.NumberFormat('en-HK', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-HK', { year: 'numeric', month: 'short', day: 'numeric' });
}

const SalaryReview: React.FC = () => {
  const [rows, setRows]           = useState<ReviewRow[]>([]);
  const [departments, setDepts]   = useState<Department[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch]       = useState('');
  const [sortBy, setSortBy]       = useState<keyof ReviewRow>('daysSinceChange');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');
  const [budgetPct, setBudgetPct] = useState(3); // global % for simulator
  const [proposed, setProposed]   = useState<Record<string, number>>({}); // employeeId → %
  const [snack, setSnack]         = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, dRes] = await Promise.all([
        apiFetch(`/api/salary-history/review-planner${deptFilter ? `?departmentId=${deptFilter}` : ''}`),
        apiFetch('/api/departments'),
      ]);
      if (!rRes.ok) throw new Error('Failed to load salary data');
      const [rData, dData] = await Promise.all([rRes.json(), dRes.json()]);
      setRows(rData);
      setDepts(dData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [deptFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getProposed = (id: string) => proposed[id] ?? budgetPct;
  const setProposedFor = (id: string, pct: number) =>
    setProposed((p) => ({ ...p, [id]: pct }));

  const filtered = useMemo(() => {
    let result = [...rows];
    if (statusFilter) result = result.filter((r) => r.reviewStatus === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
        r.department?.toLowerCase().includes(q) ||
        r.position?.toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [rows, statusFilter, search, sortBy, sortDir]);

  const handleSort = (col: keyof ReviewRow) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  // ── Budget summary ─────────────────────────────────────────────────────────

  const budgetSummary = useMemo(() => {
    const totalCurrent = filtered.reduce((s, r) => s + annualSalary(r), 0);
    const totalIncrease = filtered.reduce((s, r) => {
      const pct = getProposed(r.employeeId);
      return s + annualSalary(r) * (pct / 100);
    }, 0);
    const overdueCount = filtered.filter((r) => r.reviewStatus === 'overdue').length;
    const dueSoonCount = filtered.filter((r) => r.reviewStatus === 'due_soon').length;
    return { totalCurrent, totalIncrease, overdueCount, dueSoonCount };
  }, [filtered, proposed, budgetPct]);

  // ── Export ─────────────────────────────────────────────────────────────────

  const exportCsv = () => {
    const headers = ['Employee #','Name','Department','Position','Current Salary','Currency','Last Change','Days Since','Last % Change','Review Status','Proposed Increase %','Proposed New Salary'];
    const rws = filtered.map((r) => {
      const pct = getProposed(r.employeeId);
      const newSal = r.currentSalary ? (Number(r.currentSalary) * (1 + pct / 100)).toFixed(0) : '';
      return [
        r.employeeNumber || '',
        `${r.firstName} ${r.lastName}`,
        r.department || '',
        r.position || '',
        r.currentSalary || '',
        r.currency || '',
        fmtDate(r.lastChangeDate),
        r.daysSinceChange ?? '',
        r.lastChangePercent ?? '',
        r.reviewStatus,
        pct,
        newSal,
      ].map(String).map((v) => v.includes(',') ? `"${v}"` : v).join(',');
    });
    const csv = [headers.join(','), ...rws].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `salary-review-${new Date().toISOString().split('T')[0]}.csv`; a.click();
    URL.revokeObjectURL(url);
    setSnack(`Exported ${filtered.length} rows`);
  };

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Salary Review Planner</Typography>
          <Typography variant="body2" color="text.secondary">
            Identify overdue reviews, simulate increases, and export a proposal
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FileDownload />} onClick={exportCsv} disabled={filtered.length === 0}>
          Export Plan
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #d32f2f' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="error">{budgetSummary.overdueCount}</Typography>
              <Typography variant="caption">⚠️ Overdue Reviews</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #f57c00' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">{budgetSummary.dueSoonCount}</Typography>
              <Typography variant="caption">🕐 Due Within 60 Days</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #1976d2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="primary">{filtered.length}</Typography>
              <Typography variant="caption">👥 Employees in View</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card sx={{ borderLeft: '4px solid #388e3c' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {fmt(budgetSummary.totalIncrease)}
              </Typography>
              <Typography variant="caption">💰 Total Increase Cost (annual)</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Budget simulator */}
      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          💡 Budget Simulator — apply a global % to all employees, then override individually
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" gutterBottom>Global increase: <strong>{budgetPct}%</strong></Typography>
            <Slider
              value={budgetPct}
              onChange={(_, v) => setBudgetPct(v as number)}
              min={0} max={20} step={0.5}
              marks={[{ value: 0, label: '0%' }, { value: 5, label: '5%' }, { value: 10, label: '10%' }, { value: 20, label: '20%' }]}
              color="primary"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 8 }}>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">Current total payroll (annual)</Typography>
                <Typography variant="h6" fontWeight={700}>{fmt(budgetSummary.totalCurrent)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total increase cost</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">+{fmt(budgetSummary.totalIncrease)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">New total payroll</Typography>
                <Typography variant="h6" fontWeight={700}>{fmt(budgetSummary.totalCurrent + budgetSummary.totalIncrease)}</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Department</InputLabel>
          <Select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} label="Department">
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="due_soon">Due Soon</MenuItem>
            <MenuItem value="recent">Recent</MenuItem>
            <MenuItem value="no_data">No Data</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="Search name / dept…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><FilterAlt fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filtered.length} of {rows.length} employees
        </Typography>
      </Box>

      {/* Table */}
      <Paper sx={{ overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Position</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel active={sortBy === 'currentSalary'} direction={sortBy === 'currentSalary' ? sortDir : 'asc'} onClick={() => handleSort('currentSalary')}>
                    Current Salary
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <TableSortLabel active={sortBy === 'daysSinceChange'} direction={sortBy === 'daysSinceChange' ? sortDir : 'asc'} onClick={() => handleSort('daysSinceChange')}>
                    Last Review
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Δ%</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Proposed Increase</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>New Salary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row) => {
                const cfg = STATUS_CONFIG[row.reviewStatus];
                const pct = getProposed(row.employeeId);
                const newSalary = row.currentSalary ? Number(row.currentSalary) * (1 + pct / 100) : null;
                return (
                  <TableRow key={row.employeeId} hover sx={{ bgcolor: row.reviewStatus === 'overdue' ? 'error.50' : undefined }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.firstName} {row.lastName}</Typography>
                      {row.employeeNumber && <Typography variant="caption" color="text.secondary">#{row.employeeNumber}</Typography>}
                    </TableCell>
                    <TableCell>{row.department || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.position || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.currentSalary ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {fmt(Number(row.currentSalary), row.currency || 'HKD')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{row.frequency || 'monthly'}</Typography>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.lastChangeDate ? (
                        <Box>
                          <Typography variant="body2">{fmtDate(row.lastChangeDate)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.daysSinceChange != null ? `${Math.floor(row.daysSinceChange / 30)}mo ago` : ''}
                          </Typography>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" />
                    </TableCell>
                    <TableCell>
                      {row.lastChangePercent != null ? (
                        <Typography variant="body2" color={row.lastChangePercent > 0 ? 'success.main' : 'error'}>
                          {row.lastChangePercent > 0 ? '+' : ''}{row.lastChangePercent}%
                        </Typography>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Slider
                          value={pct}
                          onChange={(_, v) => setProposedFor(row.employeeId, v as number)}
                          min={0} max={30} step={0.5}
                          sx={{ width: 90, color: pct > 10 ? 'warning.main' : 'primary.main' }}
                          size="small"
                        />
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 36 }}>{pct}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {newSalary && pct > 0 ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {fmt(newSalary, row.currency || 'HKD')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{fmt(newSalary - Number(row.currentSalary), row.currency || 'HKD')}
                          </Typography>
                        </Box>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" py={3}>No employees match the current filters.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default SalaryReview;
